import { useEffect, useState } from "react";
import FloorService from "../../../services/floor/FloorService";
import IFloorResponse from "../../../models/floor/floorResponse";
import { useTranslation } from "react-i18next";
import DataTable, { IColumn } from "../../../components/table/data/DataTable";
import NoMapIcon from "@mui/icons-material/DoDisturbAltOutlined";
import { Box, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../routes";
import Toolbar from "../../../components/toolbar/Toolbar";
import DownloadTable from "../../../utils/download/downloadDataTable";
import { IFilter } from "../../../components/table/data/filter/DataFilter";

interface SVGImageProps {
    url: string;
    style?: React.CSSProperties;
}

const SVGImage = ({ url, style }: SVGImageProps) => {
    const [dataUrl, setDataUrl] = useState<string | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchAndConvertSVG = async () => {
            try {
                const response = await fetch(url);
                const svgText = await response.text();
                const base64 = btoa(svgText);
                setDataUrl(`data:image/svg+xml;base64,${base64}`);
            } catch (err) {
                console.error("Error loading SVG:", err);
                setError(true);
            }
        };

        fetchAndConvertSVG();
    }, [url]);

    if (error) return <NoMapIcon sx={{ fontSize: 40 }} />;
    if (!dataUrl) return <div style={{ width: 60, height: 60 }} />;

    return (
        <img
            src={dataUrl}
            alt="Floor map"
            style={{
                ...style,
                width: "100%",
                height: "100%",
                objectFit: "contain",
            }}
        />
    );
};
const useFloorColumns = () => {
    const { t } = useTranslation();

    const columns: IColumn<IFloorResponse>[] = [
        { field: "floorName", headerName: t("table.floorName") },
        {
            field: "floorNumber",
            headerName: t("table.floorNumber"),
            transform: {
                render: (data) => (
                    <>
                        {data.floorNumber}
                    </>
                ),
                filterTransform: (data) => {
                    return data.floorNumber.toString()
                }
            }
        },
        { field: "buildingName", headerName: t("table.buildingName") },
        {
            field: "floorURL",
            headerName: t("table.hbscanMap"),
            transform: {
                render: (data) => (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            width: 160,  // Landscape width
                            height: 90,  // Landscape height (16:9 ratio)
                            position: "relative",
                        }}
                    >
                        {!data.floorURL ? (
                            <NoMapIcon sx={{ fontSize: 40 }} />
                        ) : (
                            <Tooltip
                                title={
                                    <Box
                                        sx={{
                                            width: 400,  // Landscape preview
                                            height: 225, // 16:9 ratio
                                            bgcolor: "background.paper",
                                            "& img": {
                                                objectFit: "contain",
                                                width: "100%",
                                                height: "100%",
                                            }
                                        }}
                                    >
                                        <SVGImage url={data.floorURL} />
                                    </Box>
                                }
                                placement="right"
                                arrow
                                disableInteractive
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: "transparent",
                                            padding: 0,
                                            boxShadow: "none",
                                            border: "none",
                                        },
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        cursor: "pointer",
                                        bgcolor: "background.paper",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        "&:hover": {
                                            outline: "2px solid",
                                            outlineColor: "primary.main",
                                        },
                                        "& img": {
                                            objectFit: "cover",
                                            width: "100%",
                                            height: "100%",
                                            transform: "scale(1.1)", // Zoom effect
                                            transformOrigin: "center center",
                                        }
                                    }}
                                >
                                    <SVGImage
                                        url={data.floorURL}
                                        style={{
                                            position: "absolute",
                                            width: "100%",
                                            height: "100%",
                                            left: 0,
                                            top: 0,
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                ),
                filterTransform: (data) => (!data.floorURL ? "X" : "O"),
            },
        },
    ];
    return columns;
};

const FloorPage = () => {
    const [floors, setFloors] = useState<IFloorResponse[]>([]);

    const columns = useFloorColumns();
    const { t } = useTranslation([""]);
    const [isFilterActive, setIsFilterActive] = useState<boolean>(true);
    const navigate = useNavigate();

    const [filteredRows, setFilteredRows] = useState(floors);
    const [selectedFilters, setSelectedFilters] = useState<
        Map<string, IFilter>
    >(new Map());

    const visibleFilters: string[] = [];
    selectedFilters.forEach((filter, field) => {
        if (!filter.isActive || !filter.request) return;
        const column = columns.find((column) => column.field === field);
        visibleFilters.push(`${column?.headerName}: ${t(`periodical:filter.${filter.type}`)} ${filter.request}`);
    });

    const downloadTable = new DownloadTable("floor", filteredRows, columns, t("table.floor.title"), visibleFilters);

    useEffect(() => {
        FloorService.GetFloorsByCompany().then(setFloors)
    }, []);

    return (
        <Box sx={{ width: "100%", height: "100%" }}>
            <Toolbar
                onFilterClick={() => setIsFilterActive((old) => !old)}
                onCreate={() => navigate(routes.tableRoute.buildingAdd)}
                isFilterActive={isFilterActive}
                downloadButtons={[
                    { label: "Excel", onClick: () => downloadTable.GetExcel(t("header.exportedBy"), t("header.exportDate")) },
                    { label: "CSV", onClick: () => downloadTable.GetCSV() },
                    { label: "PDF", onClick: () => downloadTable.GetPDF(t("header.exportedBy"), t("header.exportDate")) }
                ]}
            />

            <DataTable
                dense
                disableFiltrer={!isFilterActive}
                rows={floors}
                columns={columns}
                onRowClick={(row) =>
                    navigate("edit", { state: { ...row } })
                }
                getRowNavTo={(row) => "edit"}
                onSetFilter={(filteredRows, selectedFilters) => {
                    setFilteredRows(filteredRows)
                    setSelectedFilters(selectedFilters)
                }}
            />
        </Box>
    );
};

export default FloorPage;
